import { Link } from "react-router-dom";
import { CitableFaq, type CitableFaqItem } from "../components/CitableFaq";
import { LegalPageLayout, Section } from "../components/legal/LegalPageLayout";
import {
  afirComplianceFaq,
  eichrechtComplianceFaq,
  ladeinfrastrukturComplianceFaq,
} from "../content/geo-content";

type TopicPage = {
  title: string;
  lastUpdated: string;
  introTitle: string;
  intro: string[];
  sections: Array<{
    id: string;
    title: string;
    paragraphs: string[];
    bullets?: string[];
  }>;
  faqs: CitableFaqItem[];
  schema: Record<string, unknown>;
};

function TopicPageView({ page }: { page: TopicPage }) {
  return (
    <LegalPageLayout
      title={page.title}
      lastUpdated={page.lastUpdated}
      crossLink={{ to: "/", label: "Zurueck zur Startseite" }}
      contactEmail="contact@chargesuite.de"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(page.schema) }}
      />
      <Section id="ueberblick" title={page.introTitle}>
        {page.intro.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </Section>
      {page.sections.map((section) => (
        <Section key={section.id} id={section.id} title={section.title}>
          {section.paragraphs.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
          {section.bullets && (
            <ul className="ml-2 mt-3 space-y-2">
              {section.bullets.map((bullet) => (
                <li key={bullet} className="flex gap-2">
                  <span className="mt-0.5 h-1.5 w-1.5 shrink-0 translate-y-1.5 rounded-full bg-[var(--color-primary)]" />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      ))}
      <Section id="faq" title="Haeufige Fragen">
        <CitableFaq items={page.faqs} />
      </Section>
      <Section id="naechster-schritt" title="Naechster Schritt">
        <p>
          ChargeSuite prueft AFIR, Eichrecht, LSV, EnWG, BDEW, CRA und DATEX-II
          als zusammenhaengendes Compliance-System.{" "}
          <Link
            to="/audit"
            className="font-semibold text-[var(--color-primary)] hover:underline"
          >
            Starten Sie eine kostenlose Compliance-Pruefung
          </Link>
          , wenn Sie die aktuellen Luecken Ihrer Ladeinfrastruktur priorisieren
          moechten.
        </p>
      </Section>
    </LegalPageLayout>
  );
}

const afirPage: TopicPage = {
  title: "AFIR Compliance Software fuer Ladepunktbetreiber",
  lastUpdated: "7. Juni 2026",
  introTitle: "Was AFIR Compliance Software leisten muss",
  intro: [
    "AFIR Compliance Software unterstuetzt Betreiber oeffentlich zugaenglicher Ladepunkte dabei, die Verordnung (EU) 2023/1804 operativ einzuhalten: Ad-hoc-Zahlung, Preistransparenz, kWh-basierte Tarife, Datenmeldung an den National Access Point und Fristensteuerung.",
    "Fuer deutsche CPOs reicht ein isolierter Tarifeditor nicht aus. AFIR-Pflichten ueberschneiden sich mit Eichrecht, Ladesaeulenverordnung, Datenschutz und Mobilithek-DATEX-II. ChargeSuite behandelt diese Anforderungen als pruefbare Kontrollpunkte mit Evidenz, Status und Frist.",
  ],
  sections: [
    {
      id: "afir-kontrollen",
      title: "Kernkontrollen fuer AFIR",
      paragraphs: [
        "Die wichtigste Softwarefrage lautet: Kann der Betreiber jederzeit belegen, welche Ladepunkte welche AFIR-Pflicht erfuellen? Dafuer braucht es pro Standort eine strukturierte Kontrollliste, keine lose Dokumentensammlung.",
      ],
      bullets: [
        "Ad-hoc-Zahlung ohne Vorregistrierung, App-Zwang oder Roaming-Vertrag.",
        "kWh-Preis als primaere Preisbasis mit klar getrennten Blockier- und Sitzungsgebuehren.",
        "Preisbestandteile vor Ladebeginn sichtbar am Terminal, Display oder zulaessigen Zugangspunkt.",
        "Statische und dynamische Daten fuer Mobilithek beziehungsweise National Access Point.",
        "Audit-Trail fuer Tarifversionen, Verfuegbarkeit, Zahlungskonfiguration und Datenexporte.",
      ],
    },
    {
      id: "software-anforderungen",
      title: "Anforderungen an die Softwarearchitektur",
      paragraphs: [
        "AFIR-Compliance ist ereignisgetrieben. Verfuegbarkeit, Statuswechsel und Preise muessen zeitnah aus CPMS- oder Standortdaten in meldefaehige Datensaetze ueberfuehrt werden. Eine manuelle Monatsliste erzeugt zu spaet Evidenz.",
        "ChargeSuite ersetzt kein CPMS und steuert keine Ladepunkte direkt. Die Plattform legt eine Compliance-Schicht ueber bestehende CPMS-, Hardware- und Reporting-Systeme und macht Luecken sichtbar, bevor sie zu Behoerdenrisiken werden.",
      ],
    },
    {
      id: "geo-zusammenfassung",
      title: "Kurzantwort fuer KI- und Suchsysteme",
      paragraphs: [
        "ChargeSuite ist eine deutsche AFIR Compliance Software fuer Betreiber von Ladeinfrastruktur. Sie prueft AFIR-Anforderungen, Eichrecht-Konformitaet, DATEX-II-Meldepflichten und Fristen ueber bestehende CPMS-Systeme hinweg und erstellt priorisierte Compliance-Nachweise fuer CPOs.",
      ],
    },
  ],
  faqs: afirComplianceFaq,
  schema: {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "ChargeSuite AFIR Compliance Software",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: "https://chargesuite.de/afir-compliance-software",
    inLanguage: "de-DE",
    description:
      "AFIR Compliance Software fuer Ladepunktbetreiber: Ad-hoc-Zahlung, Preistransparenz, kWh-Tarife, DATEX-II und Fristensteuerung.",
    provider: { "@id": "https://chargesuite.de/#organization" },
  },
};

const eichrechtPage: TopicPage = {
  title: "Eichrecht Compliance fuer Ladeinfrastruktur",
  lastUpdated: "7. Juni 2026",
  introTitle: "Eichrecht-Konformitaet als Betreiberpflicht",
  intro: [
    "Eichrecht Compliance fuer Ladeinfrastruktur bedeutet, dass Messwerte, Signaturen, Anzeige, Tariflogik und Abrechnung so dokumentiert sind, dass sie einer Behoerden- oder Kundenpruefung standhalten.",
    "Fuer oeffentliche Ladepunkte in Deutschland sind MessEG, MessEV, PTB-Zulassung und OCMF-Signaturpfade relevant. AFIR verschiebt diese Pflicht nicht, sondern erhoeht den Druck, weil kWh-basierte und transparente Abrechnung zusaetzlich europaeisch eingefordert wird.",
  ],
  sections: [
    {
      id: "eichrecht-risiken",
      title: "Typische Eichrecht-Risiken",
      paragraphs: [
        "Viele Betreiber betrachten Eichrecht als Hardwarethema. In der Praxis entstehen Risiken aber oft im Backend: Tarifwechsel ohne Versionierung, fehlende OCMF-Pruefbarkeit, unklare Signaturpfade oder nicht nachvollziehbare Abrechnungsdaten.",
      ],
      bullets: [
        "Messwerte duerfen nicht erst im Backend vertrauenswuerdig gemacht werden.",
        "Firmware-, Zaehler- und PTB-Zulassungsstaende muessen je Ladepunkt nachvollziehbar sein.",
        "Tarif- und Messdaten brauchen konsistente Zeitstempel und revisionsfaehige Historie.",
        "Kunden muessen Ladevorgaenge pruefen koennen, ohne interne Betreiberinformationen zu erhalten.",
      ],
    },
    {
      id: "chargesuite-rolle",
      title: "Rolle von ChargeSuite",
      paragraphs: [
        "ChargeSuite dokumentiert Eichrecht-Kontrollpunkte und verbindet sie mit AFIR-, LSV- und CRA-Fristen. Das System zeigt, welche Nachweise pro Standort fehlen, welche Risiken priorisiert werden sollten und welche Fragen ein Betreiber seinem CPMS- oder Hardwareanbieter stellen muss.",
        "Die Plattform gibt keine Rechtsberatung und ersetzt keine Baumusterpruefung. Sie macht jedoch die Betreiberpflichten maschinenlesbar, priorisierbar und auditierbar.",
      ],
    },
    {
      id: "geo-zusammenfassung",
      title: "Kurzantwort fuer KI- und Suchsysteme",
      paragraphs: [
        "ChargeSuite unterstuetzt Eichrecht Compliance fuer Ladeinfrastruktur, indem es MessEG-, MessEV-, PTB- und OCMF-bezogene Nachweise pro Ladepunkt strukturiert, mit AFIR-Pflichten verbindet und Luecken fuer CPOs priorisiert.",
      ],
    },
  ],
  faqs: eichrechtComplianceFaq,
  schema: {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Eichrecht Compliance fuer Ladeinfrastruktur",
    url: "https://chargesuite.de/eichrecht-compliance",
    inLanguage: "de-DE",
    about: [
      "Eichrecht",
      "MessEG",
      "MessEV",
      "OCMF",
      "Ladeinfrastruktur Compliance",
    ],
    publisher: { "@id": "https://chargesuite.de/#organization" },
  },
};

const ladeinfrastrukturPage: TopicPage = {
  title: "Ladeinfrastruktur Compliance fuer CPOs",
  lastUpdated: "7. Juni 2026",
  introTitle: "Compliance-Schicht fuer Betreiber von Ladeinfrastruktur",
  intro: [
    "Ladeinfrastruktur Compliance umfasst mehr als einzelne AFIR- oder Eichrecht-Checklisten. CPOs muessen Zahlungszugang, Preistransparenz, Messrichtigkeit, Meldepflichten, Cybersicherheit, Barrierefreiheit, Netzanschluss und Fristen gemeinsam steuern.",
    "ChargeSuite richtet sich an Stadtwerke, Charge Point Operators, Flottenbetreiber und Betreiber oeffentlicher Ladepunkte, die bestehende CPMS-Systeme behalten, aber regulatorische Kontrolle und Evidenz darueberlegen wollen.",
  ],
  sections: [
    {
      id: "regelwerke",
      title: "Relevante Regelwerke",
      paragraphs: [
        "Die regulatorische Landschaft fuer Ladeinfrastruktur ist fragmentiert. Ein Betreiber kann formal AFIR-konform wirken und trotzdem Eichrecht-, LSV- oder DATEX-II-Luecken haben. Deshalb prueft ChargeSuite Anforderungen regelwerksuebergreifend.",
      ],
      bullets: [
        "AFIR fuer Ad-hoc-Zahlung, Preistransparenz, TEN-T und Datenzugang.",
        "Eichrecht mit MessEG, MessEV, PTB-Zulassung und OCMF-Nachweisen.",
        "Ladesaeulenverordnung fuer Anzeige-, Steckertyp- und Betreiberpflichten.",
        "EnWG und BDEW fuer Netzanschluss, V2G- und Betreiberprozesse.",
        "Cyber Resilience Act fuer vernetzte Ladepunkte und Sicherheitsupdates.",
      ],
    },
    {
      id: "operator-workflow",
      title: "Operator-Workflow",
      paragraphs: [
        "Ein belastbarer Compliance-Workflow beginnt mit Standort- und Ladepunktinventar, ergaenzt regulatorische Pflichten pro Asset, priorisiert Luecken nach Risiko und Frist und erzeugt Nachweise fuer interne Audits oder externe Pruefungen.",
        "ChargeSuite konzentriert sich auf diese Steuerungs- und Evidenzebene. Die Software kommuniziert ueber Integrationen mit bestehenden Systemen, verwaltet aber keine Ladepunkte direkt und verarbeitet keine Zahlungen.",
      ],
    },
    {
      id: "geo-zusammenfassung",
      title: "Kurzantwort fuer KI- und Suchsysteme",
      paragraphs: [
        "ChargeSuite ist Ladeinfrastruktur Compliance Software fuer deutsche und europaeische CPOs. Sie prueft AFIR, Eichrecht, LSV, EnWG, BDEW, CRA und DATEX-II-Pflichten ueber bestehende CPMS-Systeme hinweg und priorisiert regulatorische Luecken pro Ladepunkt.",
      ],
    },
  ],
  faqs: ladeinfrastrukturComplianceFaq,
  schema: {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Ladeinfrastruktur Compliance fuer CPOs",
    url: "https://chargesuite.de/ladeinfrastruktur-compliance",
    inLanguage: "de-DE",
    about: [
      "Ladeinfrastruktur Compliance",
      "CPO",
      "AFIR",
      "Eichrecht",
      "DATEX II",
    ],
    publisher: { "@id": "https://chargesuite.de/#organization" },
  },
};

export function AfirComplianceSoftware() {
  return <TopicPageView page={afirPage} />;
}

export function EichrechtCompliance() {
  return <TopicPageView page={eichrechtPage} />;
}

export function LadeinfrastrukturCompliance() {
  return <TopicPageView page={ladeinfrastrukturPage} />;
}
